import { gql, useQuery } from '@apollo/client';
import type { DriftCalculationMethod } from '@generated/graphql';

// Deliberately not in a *.queries.ts file: codegen globs
// workflowCompletion/**/*.queries.ts, and every drift document in this epic
// stays hand-typed so it cannot break typecheck on an environment where the
// backend fields are not deployed yet.
const FETCH_ORG_DRIFT_DEFAULTS = gql`
  query fetchOrganizationDriftDefaults($organizationId: String!) {
    fetchOrganization(organizationId: $organizationId) {
      organization {
        id
        defaultDriftCalculationMethod
        defaultDriftTolerancePercent
      }
    }
  }
`;

export type OrgDriftDefaults = {
  method: DriftCalculationMethod | null;
  tolerancePercent: number | null;
  /**
   * Whether the organization's defaults were actually read.
   *
   * Distinct from "the organization has no default set": that is a resolved
   * answer, and the system default is genuinely what a null field inherits,
   * so naming it is correct. Unresolved — the query skipped, still in flight,
   * or the caller cannot read the organization — means we do not know, and
   * naming a value there presents a guess as fact.
   */
  resolved: boolean;
};

/**
 * The organization's defaults, shown as the "Inherited (currently X)" option
 * and the tolerance placeholder.
 *
 * Display only. These values are never written into the model — leaving a
 * field null keeps it inheriting live, so a later change to the org default
 * follows through instead of being frozen at creation time.
 */
export const useOrgDriftDefaults = ({
  organizationId,
  enabled,
}: {
  organizationId: string | undefined;
  enabled: boolean;
}): OrgDriftDefaults => {
  const { data, loading } = useQuery(FETCH_ORG_DRIFT_DEFAULTS, {
    variables: { organizationId },
    fetchPolicy: 'no-cache',
    skip: !enabled || !organizationId,
  });

  const org = data?.fetchOrganization?.organization;

  return {
    method: org?.defaultDriftCalculationMethod ?? null,
    tolerancePercent: org?.defaultDriftTolerancePercent ?? null,
    // Tied to the organization that was asked for, not to data being present:
    // a result still held from a previous organizationId would otherwise name
    // another organization's defaults as this one's while the new read is in
    // flight — the same skew useReportDriftSettings guards by report id.
    resolved: !!organizationId && !loading && org?.id === organizationId,
  };
};
